import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { solutionSettings } from '../../data/solutions';
import { demoUrlFor, getDemoDomain } from '../../data/demoCenter';
import { fadeUp } from '../../utils/motion';
export function SolutionSettings() {
  const scrollTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="bg-white py-20 lg:py-28" data-id="element-2516" data-mp="3ap53r1ailu4c">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2517" data-mp="2vzqwbyqi1mrl">
        <nav aria-label="Solution settings" className="hidden lg:block lg:col-span-3" data-id="element-2518" data-mp="30y0tgevsaxfu">
          <div className="sticky top-28" data-id="element-2519" data-mp="2mz1578c6okk7">
            <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-2520" data-mp="2n03whhdf4p0n">Settings</p>
            <ul className="border-l border-line" data-id="element-2521" data-mp="2ry5es030skb2">
              {solutionSettings.map(item => <li key={item.id} data-id="element-2522" data-mp="1op3b907q1i0b">
                  <a href={`#${item.id}`} onClick={scrollTo(item.id)} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-2523" data-mp="1oobh7ttrvsc5">
                    {item.setting.replace('For ', '')}
                  </a>
                </li>)}
            </ul>
          </div>
        </nav>

        <div className="lg:col-span-9" data-id="element-2524" data-mp="111lf6shfbxrj">
          {solutionSettings.map(item => <motion.article key={item.id} id={item.id} {...fadeUp} className="scroll-mt-28 border-t border-line py-14 first:border-t-0 first:pt-0" data-id="element-2525" data-mp="1fpy9zm4rzakg">
              <ul aria-label="Supporting components" className="flex flex-wrap gap-2 mb-4" data-id="element-2526" data-mp="n83ydfg4gz57">
                {item.components.map(component => <li key={component} className="text-xs font-medium text-teal border border-line rounded px-2 py-1 whitespace-nowrap" data-id="element-2527" data-mp="i87ztvq71zx1">
                    {component}
                  </li>)}
              </ul>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight mb-3" data-id="element-2528" data-mp="2s47xwn5cgav5">
                {item.setting}
              </h2>
              <p className="text-xl text-ink-dark mb-8" data-id="element-2529" data-mp="1zehhg7g8owvv">{item.tagline}</p>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-12" data-id="element-2530" data-mp="24c0vb690z7na">
                <div className="xl:col-span-8 space-y-5" data-id="element-2531" data-mp="28e4ao5ytewdu">
                  <div className="rounded-lg border border-line bg-surface-bg p-5" data-id="element-2532" data-mp="28cktmd5c4hb0">
                    <p className="text-sm font-semibold text-teal" data-id="element-2533" data-mp="2de0dw2zoujsc">
                      {getDemoDomain(item.demoId)?.status === 'scoped' ? 'Scoped interactive demo' : 'Interactive demo'} · fictional records
                    </p>
                    <p className="mt-1 text-ink-dark leading-relaxed" data-id="element-2534" data-mp="3ougwg56jhgjf">{item.demo}</p>
                    <a href={demoUrlFor(item.demoId)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2535" data-mp="2q584lxee3ma">
                      {getDemoDomain(item.demoId)?.actionLabel}
                      <ArrowUpRight size={16} aria-hidden="true" data-id="element-2536" data-mp="3g3qg5togoz70" />
                      <span className="sr-only" data-id="element-2537" data-mp="3b5yq757dnn5g">(opens in a new tab)</span>
                    </a>
                  </div>
                  <p className="text-sm font-semibold text-ink-muted" data-id="element-2538" data-mp="367x6ci24njsx">Proposed production use</p>
                  {item.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-2539" data-mp="14gk2zxft5jyz">
                      {paragraph}
                    </p>)}
                  {item.safeguard && <div className="flex gap-4 bg-surface-muted rounded-lg p-5" data-id="element-2540" data-mp="17iwztzgzyxhm">
                      <ShieldCheck size={20} className="text-navy-light shrink-0 mt-1" aria-hidden="true" data-id="element-2541" data-mp="1chhyhuglp93y" />
                      <p className="text-ink-dark leading-relaxed" data-id="element-2542" data-mp="yhyr53vm61sp">{item.safeguard}</p>
                    </div>}
                </div>

                <aside className="xl:col-span-4" data-id="element-2543" data-mp="12kn2bi7svksu">
                  <h3 className="text-sm font-semibold text-ink-dark mb-3" data-id="element-2544" data-mp="oqcjtimecmv8">Possible applications</h3>
                  <ul className="border-t border-line mb-6" data-id="element-2545" data-mp="tj5ns4a1jr69">
                    {item.applications.map(application => <li key={application} className="border-b border-line py-2.5 text-ink-muted" data-id="element-2546" data-mp="ev13wwz959lw">
                        {application}
                      </li>)}
                  </ul>
                  <a href={item.link} className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2547" data-mp="5umhfuajxogt">
                    {item.linkLabel}
                    <ArrowRight size={16} data-id="element-2548" data-mp="3idfdeqy37sl7" />
                  </a>
                </aside>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}